// The admin bell's data — and what opening a notification does (0278).
//
// notifications (0011) grants every interactive role SELECT + UPDATE(read_at),
// RLS-scoped to `user_id = auth.uid()` — a plain client read/write, no Edge
// Function needed (API Conventions: no cross-table rule here).

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { adminNotificationHref, notificationLevel, notificationTitle } from "@bednbody/logic";
import { useAuth } from "@/lib/auth";
import { drillInState } from "@/lib/drillIn";
import { canAccess } from "@/lib/permissions";
import { supabase } from "@/lib/supabase";
import type { AdminNotification } from "./notifications.model";

export type { AdminNotification } from "./notifications.model";

const NOTIFICATIONS_KEY = ["notifications"] as const;

/**
 * Live: a new row reaches the bell in a second, not on the next poll (the poll
 * stays as a fallback for a dropped socket). Mount ONCE — AppLayout does — so
 * there is one channel per signed-in user however many readers there are.
 */
export function useNotificationsRealtime() {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`notifications-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        () => void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);
}

export function useNotifications() {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;

  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    enabled: userId !== null,
    queryFn: async (): Promise<AdminNotification[]> => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, event, body, read_at, created_at, subject_type, subject_id")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        event: row.event,
        title: notificationTitle(row.event),
        body: row.body,
        level: notificationLevel(row.event),
        readAt: row.read_at,
        createdAt: row.created_at ?? new Date(0).toISOString(),
        href: adminNotificationHref(row.event, { type: row.subject_type, id: row.subject_id }),
      }));
    },
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    // RLS scopes the update to this user's own rows.
    mutationFn: async () => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

/**
 * Open a notification: mark it read and go where it points, carrying where the
 * user came from so the header offers "← Back to …".
 *
 * The link is checked against the user's own pages first — a Hub Manager is
 * never sent to a page the route guard would bounce (the destination is a
 * suggestion, never a permission). Falls back to the dashboard.
 */
export function useOpenNotification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { claims } = useAuth();
  const markRead = useMarkNotificationRead();

  return (notification: Pick<AdminNotification, "id" | "href" | "readAt">) => {
    if (!notification.readAt) markRead.mutate(notification.id);
    const path = notification.href.split("?")[0] ?? "/";
    const allowed = claims ? canAccess(claims.role, path) : false;
    const from = `${location.pathname}${location.search}`;
    navigate(allowed ? notification.href : "/", { state: drillInState(from) });
  };
}
